import { action, makeObservable, observable } from "mobx";
import { IPromiseBasedObservable, fromPromise } from "mobx-utils";
import AuthStore from "../Auth/authStore";
import notificationStore from "../Notification/notificationStore";
import { BiApi } from "../../api-client";
import { AxiosResponse } from "axios";

export default class BIStore {
  public apiClient: BiApi;
  public authStore: AuthStore;

  public constructor(apiClient: BiApi, authStore: AuthStore) {
    makeObservable(this);
    this.apiClient = apiClient;
    this.authStore = authStore;
  }

  // The Controlling dashboard mounts ~12 hooks that each fire a stats endpoint on load.
  // Letting them all hit the API simultaneously loads many large result sets into memory at
  // once on the server and reliably OOM-crashed the (clustered, ~960MB-per-worker) API.
  //
  // The stats endpoints fall into two groups:
  //  - "heavy" ones hydrate large entity graphs (all treatments/Rx in the range with deep
  //    joins) and dominate server memory. We run these strictly one-at-a-time, since even two
  //    concurrently could exceed a worker's heap on real-sized data.
  //  - the rest are comparatively cheap and may run a few at a time to keep the page snappy.
  //
  // Two independent pools mean a page load runs as "1 heavy request + a few light ones" at any
  // moment, which keeps peak memory bounded while remaining much faster than fully serial.
  private static readonly MAX_HEAVY_REQUESTS = 1;
  private static readonly MAX_LIGHT_REQUESTS = 3;
  private static readonly pools = {
    heavy: { active: 0, max: BIStore.MAX_HEAVY_REQUESTS, queue: [] as Array<() => void> },
    light: { active: 0, max: BIStore.MAX_LIGHT_REQUESTS, queue: [] as Array<() => void> },
  };

  private static acquireSlot(pool: { active: number; max: number; queue: Array<() => void> }): Promise<void> {
    if (pool.active < pool.max) {
      pool.active++;
      return Promise.resolve();
    }
    return new Promise<void>((resolve) =>
      pool.queue.push(() => {
        pool.active++;
        resolve();
      })
    );
  }

  private static releaseSlot(pool: { active: number; max: number; queue: Array<() => void> }): void {
    pool.active--;
    const next = pool.queue.shift();
    if (next) next();
  }

  // Runs the given request through the shared BI concurrency limiter. Pass heavy=true for
  // endpoints that load large entity graphs so they never overlap with each other.
  private throttle = async <T>(fn: () => Promise<T>, heavy = false): Promise<T> => {
    const pool = heavy ? BIStore.pools.heavy : BIStore.pools.light;
    await BIStore.acquireSlot(pool);
    try {
      return await fn();
    } finally {
      BIStore.releaseSlot(pool);
    }
  };

  // add properties here that will behave like state variables

  // Also we wrap our APIs here:

  // @action - do we really need action here?

  public getPatientStats = async (startDate: Date, endDate: Date, locationIds?: string[]) => {
    try {
      const { data } = await this.throttle(() =>
        this.apiClient.getPatientStats(startDate.toISOString(), endDate.toISOString(), locationIds)
      );
      return data || {patientStats: [],
        fallbackUsed: false,
        usedStartDate: undefined,
        usedEndDate: undefined};
    } catch (error: any) {
      const errorMessage = error.response.data.message || "Problem fetching patient stats";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  public getRxBasicStats = async (startDate: Date, endDate: Date, locationIds?: string[], rxType?: string) => {
    try {
      const { data } = await this.throttle(
        () => this.apiClient.getRxBasicStats(startDate.toISOString(), endDate.toISOString(), locationIds, rxType),
        true
      );
      return data || {rxStats: [],
        fallbackUsed: false,
        usedStartDate: undefined,
        usedEndDate: undefined};
    } catch (error: any) {
      const errorMessage = error.response.data.message || "Problem fetching Rx basic stats";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  public getRxLifespanStats = async (startDate: Date, endDate: Date, locationIds?: string[], rxType?: string) => {
    try {
      const { data } = await this.throttle(() =>
        this.apiClient.getRxLifespanStats(startDate.toISOString(), endDate.toISOString(), locationIds, rxType)
      );
      return data || [];
    } catch (error: any) {
      const errorMessage = error.response.data.message || "Problem fetching Rx lifespan stats";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  public getRxTherapistsStats = async (startDate: Date, endDate: Date, locationIds?: string[], rxType?: string) => {
    try {
      const { data } = await this.throttle(
        () => this.apiClient.getRxTherapistsStats(startDate.toISOString(), endDate.toISOString(), locationIds, rxType),
        true
      );
      return data || [];
    } catch (error: any) {
      const errorMessage = error.response.data.message || "Problem fetching Rx therapists stats";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  public getTherapyRxFrequencyStats = async (startDate: Date, endDate: Date, locationIds?: string[]) => {
    try {
      const { data } = await this.throttle(() =>
        this.apiClient.getTherapyRxFrequencyStats(startDate.toISOString(), endDate.toISOString(), locationIds)
      );
      return data || [];
    } catch (error: any) {
      const errorMessage = error.response.data.message || "Problem fetching therapy Rx frequency stats";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  public getRxBlankoStats = async (startDate: Date, endDate: Date, locationIds?: string[]) => {
    try {
      const { data } = await this.throttle(() =>
        this.apiClient.getRxBlankoStats(startDate.toISOString(), endDate.toISOString(), locationIds)
      );
      return data || [];
    } catch (error: any) {
      const errorMessage = error.response.data.message || "Problem fetching Rx Blanko stats";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  public getRxBlankoLifespanStats = async (startDate: Date, endDate: Date, locationIds?: string[]) => {
    try {
      const { data } = await this.throttle(() =>
        this.apiClient.getRxBlankoLifespanStats(startDate.toISOString(), endDate.toISOString(), locationIds)
      );
      return data || [];
    } catch (error: any) {
      const errorMessage = error.response.data.message || "Problem fetching Rx Blanko lifespan stats";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  public getRxBlankoWithTherapistsStats = async (startDate: Date, endDate: Date, locationIds?: string[]) => {
    try {
      const { data } = await this.throttle(
        () => this.apiClient.getRxBlankoWithTherapistsStats(startDate.toISOString(), endDate.toISOString(), locationIds),
        true
      );
      return data || [];
    } catch (error: any) {
      const errorMessage = error.response.data.message || "Problem fetching Rx Blanko therapist stats";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  public getScheduleBasedStats = async (startDate: Date, endDate: Date, locationIds?: string[]) => {
    try {
      const { data } = await this.throttle(
        () => this.apiClient.getScheduleBasedStats(startDate.toISOString(), endDate.toISOString(), locationIds),
        true
      );
      return data || [];
    } catch (error: any) {
      const errorMessage = error.response.data.message || "Problem fetching schedule-based stats";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  public getTherapistStats = async (startDate: Date, endDate: Date) => {
    try {
      const { data } = await this.throttle(
        () => this.apiClient.getTherapistStats(startDate.toISOString(), endDate.toISOString()),
        true
      );
      return data || [];
    } catch (error: any) {
      const errorMessage = error.response.data.message || "Problem fetching therapist stats";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  };

  public getMonthlyRevenue = async (date?: Date, locationIds?: string[]) => {
    try {
      // apiClient signature: getMonthlyRevenue(locationIds?: Array<string>, date?: string)
      const { data } = await this.throttle(() =>
        this.apiClient.getMonthlyRevenue(locationIds, date ? date.toISOString() : undefined)
      );
      return data || [];
    } catch (error: any) {
      const errorMessage = error.response.data.message || "Problem fetching daily schedule revenue";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  }

  public sendBIReportCSVEmail = async (csvData: string, filename: string, subject: string, recipientEmail: string) => {
    try {
      await this.apiClient.sendBIReportCSVEmail({
        to: recipientEmail,
        subject: subject,
        csvData: csvData,
        filename: filename,
      });
      notificationStore.showMessage("CSV-Datei wurde erfolgreich per E-Mail gesendet", "success");
      return { success: true };
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "CSV-Datei konnte nicht per E-Mail gesendet werden";
      notificationStore.showMessage(errorMessage, "error", error);
      return null;
    }
  }

  public getYearEndRxUnfinishedValues = async (startDate: Date, endDate: Date) => {
    try {
      const { data } = await this.throttle(
        () => this.apiClient.getYearEndRxUnfinishedValues(startDate.toISOString(), endDate.toISOString()),
        true
      );
      return data || [];
    } catch (error: any) {
      const errorMessage = error.response.data.message || "Problem fetching year-end Rx unfinished values";
      notificationStore.showMessage(errorMessage, "error", error);
    }
  }
}
